import classNames from 'classnames';
import { Controller, FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import { createFormFieldStyles } from '~/constants/styles';
import { FormFeedback } from '../FormFeedback';
import { FormField } from '../FormField';
import { getFieldError } from '../formUtils';
import { FormFieldProps } from '../interfaces';

interface Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends FormFieldProps<TFieldValues, TName> {
  placeholder?: string;
  autoComplete?: string;
  rows?: number;
  maxLength?: number;
}
export const TextArea = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: Props<TFieldValues, TName>
) => {
  const {
    name,
    description,
    disabled,
    cyTag,
    label,
    className,
    rows,
    maxLength,
    placeholder,
    flagged,
    required,
    tooltip,
    errorCyTag,
    reserveFeedbackSpace,
  } = props;
  const {
    formState: { errors },
  } = useFormContext();
  const error = getFieldError(errors, name);
  return (
    <Controller
      name={name}
      render={({ field }) => {
        const value = field.value || '';
        return (
          <FormField
            id={name}
            label={label}
            tooltip={tooltip}
            required={required}
            feedback={
              <FormFeedback
                error={error?.message}
                errorId={`${name}-error`}
                errorCyTag={errorCyTag ?? (cyTag ? `${cyTag}-error` : undefined)}
                description={description}
                helperId={`${name}-helper`}
                reserveSpace={reserveFeedbackSpace}
              />
            }
          >
            <div data-cy={cyTag}>
              <textarea
                rows={rows}
                maxLength={maxLength}
                disabled={disabled}
                placeholder={placeholder}
                id={name}
                {...field}
                value={value}
                className={classNames(createFormFieldStyles(flagged, disabled, !!error), className)}
                aria-invalid={!!error || undefined}
                aria-required={required || undefined}
                aria-describedby={
                  error?.message ? `${name}-error` : description ? `${name}-helper` : undefined
                }
                aria-errormessage={error?.message ? `${name}-error` : undefined}
              />
            </div>
          </FormField>
        );
      }}
    />
  );
};
